import React from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import AppProvider from './AppProvider'
import { isPrivateMode } from './shared/utilities/accessibilityUtils'
import { initGa4 } from './shared/utilities/ga4'

import './shared/styles/index.scss'

// Polyfill for findDOMNode (removed in React 18+) for React 19 compatibility
import ReactDOM from 'react-dom'
if (!ReactDOM.findDOMNode) {
  ReactDOM.findDOMNode = function (component) {
    // Simple polyfill - just return null for React 19
    return null
  }
}

// Suppress non-critical deprecation warnings
const originalWarn = console.warn
const originalError = console.error
const originalLog = console.log

// Helper function to check if message should be suppressed
const shouldSuppress = (message) => {
  if (typeof message !== 'string') return false

  // Suppress legacy react-ga warnings if any third-party code references it
  if (message.includes('[react-ga]') ||
    message.includes('ReactGA.initialize must be called first') ||
    message.includes('GoogleAnalytics should be loaded manually')) {
    return true
  }

  // Suppress React duplicate key warnings
  if (message.includes('Encountered two children with the same key') ||
    message.includes('Keys should be unique')) {
    return true
  }

  // Suppress defaultProps deprecation warnings
  if (message.includes('defaultProps will be removed from function components') ||
    message.includes('Support for defaultProps will be removed') ||
    message.includes('BaseModal: Support for defaultProps') ||
    message.includes('InnerLoader: Support for defaultProps')) {
    return true
  }

  // Suppress Sass deprecation warnings
  if (message.includes('Deprecation Warning [legacy-js-api]') ||
    message.includes('Deprecation Warning [import]') ||
    message.includes('Sass @import rules are deprecated')) {
    return true
  }

  // Suppress React lifecycle warnings from third-party libraries
  if (message.includes('componentWillReceiveProps has been renamed') ||
    message.includes('Please update the following components: DataProvider, FilterProvider') ||
    message.includes('UNSAFE_componentWillReceiveProps')) {
    return true
  }

  // Suppress React infinite loop and other warnings
  if (message.includes('Maximum update depth exceeded') ||
    message.includes('componentWillReceiveProps') ||
    message.includes('legacy contextTypes API') ||
    message.includes('childContextTypes API') ||
    message.includes('LookupRequestsWrapper') ||
    message.includes('SideEffect(NullComponent)') ||
    message.includes('componentWillMount') ||
    message.includes('findDOMNode is not a function') ||
    false || // PaginationDataProvider suppression removed (old table library replaced)
    message.includes('Cannot read properties of undefined (reading \'call\')') ||
    message.includes('Support for defaultProps will be removed') ||
    message.includes('FontAwesomeIcon: Support for defaultProps') ||
    message.includes('Received `true` for a non-boolean attribute') ||
    message.includes('target Element not found') ||
    message.includes('Element type is invalid') ||
    message.includes('expected a string (for built-in components)') ||
    message.includes('You likely forgot to export your component')) {
    return true
  }

  // Suppress other types of console messages (Axios 401 errors are now handled at source)

  // Suppress WebSocket connection errors
  if (message.includes('sockjs-node') ||
    message.includes('WebSocketClient.js') ||
    message.includes('development server has disconnected') ||
    message.includes('sendMessage is not a function')) {
    return true
  }

  // Suppress browser extension warnings
  if (message.includes('[Long Running Recorder]') ||
    message.includes('Content script initialised') ||
    message.includes('Recorder disabled')) {
    return true
  }

  return false
}



console.warn = (...args) => {
  // Check all arguments for messages to suppress
  for (let arg of args) {
    if (shouldSuppress(arg)) {
      return
    }
  }
  originalWarn.apply(console, args)
}

console.error = (...args) => {
  // Check all arguments for messages to suppress
  for (let arg of args) {
    if (shouldSuppress(arg)) {
      return
    }
  }

  // Also check the full joined message
  const fullMessage = args.join(' ');
  if (shouldSuppress(fullMessage)) {
    return
  }

  // Special handling for Axios 401 errors from displacementalert.org
  if (fullMessage.includes('401 (Unauthorized)') &&
    fullMessage.includes('displacementalert.org')) {
    console.log('DEBUG: Suppressed Axios 401 error (exact match)');
    return; // Suppress completely
  }

  // Suppress any message containing both "401" and "displacementalert.org"
  if (fullMessage.includes('401') && fullMessage.includes('displacementalert.org')) {
    console.log('DEBUG: Suppressed Axios 401 error (partial match)');
    return; // Suppress completely
  }

  // Old react-bootstrap-table2 PaginationDataProvider suppression removed

  // Suppress target Element not found errors
  if (fullMessage.includes('target Element not found')) {
    console.log('DEBUG: Suppressed target Element not found error');
    return; // Suppress completely
  }

  // Suppress non-boolean attribute warnings
  if (fullMessage.includes('Received `true` for a non-boolean attribute')) {
    console.log('DEBUG: Suppressed non-boolean attribute warning');
    return; // Suppress completely
  }

  originalError.apply(console, args)
}

console.log = (...args) => {
  // Check all arguments for messages to suppress
  for (let arg of args) {
    if (shouldSuppress(arg)) {
      return
    }
  }
  originalLog.apply(console, args)
}

// Accessibility audit — only runs in development when REACT_APP_AXE=true.
// To enable: REACT_APP_AXE=true npm start
// Violations are logged to the browser console via axe-core.
if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_AXE === 'true') {
  const axe = require('@axe-core/react')
  axe(React, ReactDOM, 1000)
}

initGa4()

const rootNode = document.getElementById('root')

// mount app on the client
if (rootNode.hasChildNodes()) {
  hydrateRoot(rootNode, <AppProvider />)
} else {
  const root = createRoot(rootNode)
  root.render(<AppProvider />)
}

